/* Live Indianly cart-removal UX | managed asset */
(() => {
  "use strict";

  const INSTANCE_KEY = "__liCartRemovalUxController";
  const MOBILE_QUERY = "(max-width: 767.98px)";

  if (window[INSTANCE_KEY]) return;

  function cleanText(value) {
    return String(value || "")
      .replace(/\s+/g, " ")
      .trim();
  }

  function cloneJson(value) {
    return JSON.parse(JSON.stringify(value));
  }

  function eventPayload(name, data) {
    return Object.assign(
      {
        event: name,
        event_id: `${name}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        event_time: new Date().toISOString(),
        page_path: window.location.pathname,
        source_component: "cart_drawer_remove_experience",
      },
      data || {}
    );
  }

  function track(name, data) {
    const payload = eventPayload(name, data);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);

    const recent = Array.isArray(window.__liAnalyticsEvents) ? window.__liAnalyticsEvents : [];
    recent.push(payload);
    if (recent.length > 50) recent.shift();
    window.__liAnalyticsEvents = recent;

    window.dispatchEvent(new CustomEvent("li:analytics", { detail: payload }));

    if (window.Shopify?.analytics?.publish) {
      try {
        window.Shopify.analytics.publish(name, payload);
      } catch (error) {}
    }
  }

  class LiveIndianlyCartRemovalUx {
    constructor(root) {
      this.root = root;
      this.layer = root.querySelector("[data-li-cart-remove-layer]");
      this.backdrop = root.querySelector("[data-li-cart-remove-backdrop]");
      this.sheet = root.querySelector("[data-li-cart-remove-sheet]");
      this.confirmView = root.querySelector("[data-li-cart-remove-confirm-view]");
      this.recoveryView = root.querySelector("[data-li-cart-remove-recovery-view]");
      this.recoveryTitle = root.querySelector("[data-li-cart-recovery-title]");
      this.title = root.querySelector("[data-li-cart-remove-title]");
      this.support = root.querySelector("[data-li-cart-remove-support]");
      this.image = root.querySelector("[data-li-cart-remove-image]");
      this.productTitle = root.querySelector("[data-li-cart-remove-product-title]");
      this.variant = root.querySelector("[data-li-cart-remove-variant]");
      this.price = root.querySelector("[data-li-cart-remove-price]");
      this.quantity = root.querySelector("[data-li-cart-remove-quantity]");
      this.confirmError = root.querySelector("[data-li-cart-remove-error]");
      this.confirmButton = root.querySelector("[data-li-cart-remove-confirm]");
      this.confirmLabel = root.querySelector("[data-li-cart-remove-confirm-label]");
      this.cancelButton = root.querySelector("[data-li-cart-remove-cancel]");
      this.closeButton = root.querySelector("[data-li-cart-remove-close]");
      this.recoveryError = root.querySelector("[data-li-cart-recovery-error]");
      this.recoveryUndo = root.querySelector("[data-li-cart-recovery-undo]");
      this.shopBaby = root.querySelector("[data-li-cart-shop-baby]");
      this.status = root.querySelector("[data-li-cart-removal-status]");
      this.statusMessage = root.querySelector("[data-li-cart-status-message]");
      this.statusUndo = root.querySelector("[data-li-cart-status-undo]");
      this.statusRetry = root.querySelector("[data-li-cart-status-retry]");
      this.statusClose = root.querySelector("[data-li-cart-status-close]");

      const requiredNodes = [
        this.layer,
        this.backdrop,
        this.sheet,
        this.confirmView,
        this.recoveryView,
        this.recoveryTitle,
        this.title,
        this.support,
        this.image,
        this.productTitle,
        this.variant,
        this.price,
        this.quantity,
        this.confirmError,
        this.confirmButton,
        this.confirmLabel,
        this.cancelButton,
        this.closeButton,
        this.recoveryError,
        this.recoveryUndo,
        this.shopBaby,
        this.status,
        this.statusMessage,
        this.statusUndo,
        this.statusRetry,
        this.statusClose,
      ];
      if (requiredNodes.some((node) => !node)) return;
      this.ready = true;

      this.cart = null;
      this.cartPromise = null;
      this.pending = null;
      this.retryPending = null;
      this.undoItem = null;
      this.restoreOperation = null;
      this.busy = false;
      this.intentResolving = false;
      this.operationEpoch = 0;
      this.destroyed = false;

      this.onRootClick = this.onRootClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onKeyup = this.onKeyup.bind(this);
      this.onCartUpdated = this.onCartUpdated.bind(this);

      root.addEventListener("click", this.onRootClick, true);
      document.addEventListener("keydown", this.onKeydown, true);
      document.addEventListener("keyup", this.onKeyup, true);
      document.addEventListener("cart:updated", this.onCartUpdated);

      this.visibilityObserver = new MutationObserver(() => {
        if (this.root.hidden) this.reset();
        else {
          this.normalizeQuantityMinimums();
          this.refreshCart().catch(() => {});
        }
      });
      this.visibilityObserver.observe(root, { attributes: true, attributeFilter: ["hidden"] });

      this.normalizeQuantityMinimums();
      this.refreshCart().catch(() => {});
    }

    isMobile() {
      return window.matchMedia(MOBILE_QUERY).matches;
    }

    isLayerOpen() {
      return !this.layer.hidden;
    }

    async refreshCart() {
      if (this.cartPromise) return this.cartPromise;

      const request = this.fetchCartSnapshot()
        .then((cart) => {
          this.cart = cart;
          return cart;
        })
        .finally(() => {
          if (this.cartPromise === request) this.cartPromise = null;
        });

      this.cartPromise = request;
      return request;
    }

    async fetchCartSnapshot() {
      const response = await fetch(`${window.FoxTheme.routes.cart_url}.js?li_cache=${Date.now()}`, {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });
      if (!response.ok) throw new Error("Could not load the cart.");
      return response.json();
    }

    onCartUpdated(event) {
      const cart = event.detail?.cart;
      if (cart?.items) this.cart = cart;
      requestAnimationFrame(() => this.normalizeQuantityMinimums());
    }

    normalizeQuantityMinimums() {
      this.root.querySelectorAll('quantity-input input.quantity__input:not([disabled])').forEach((input) => {
        const minimum = Math.max(1, Number(input.dataset.min || 1));
        input.min = String(minimum);
      });
    }

    beginOperation() {
      this.operationEpoch += 1;
      return this.operationEpoch;
    }

    isCurrentOperation(operation) {
      return operation === this.operationEpoch && !this.root.hidden;
    }

    parseLineKey(trigger) {
      try {
        return new URL(trigger.href, window.location.origin).searchParams.get("id") || "";
      } catch (error) {
        return "";
      }
    }

    async itemForTrigger(trigger) {
      const key = this.parseLineKey(trigger);
      const index = Math.max(0, Number(trigger.dataset.index || 1) - 1);
      let cart = this.cart;
      let item = cart?.items?.find((candidate) => candidate.key === key) || cart?.items?.[index];

      if (!item || (key && item.key !== key)) {
        cart = await this.refreshCart();
        item = cart.items.find((candidate) => candidate.key === key) || cart.items[index];
      }

      if (!item) throw new Error("This cart item could not be found.");
      return { cart, item: cloneJson(item), key: item.key || key, index: index + 1 };
    }

    visualForTrigger(trigger, item) {
      const row = trigger.closest(".cart-item");
      const image = row?.querySelector(".cart-item__media img");
      const variantValues = Array.from(row?.querySelectorAll(".cart-item__option-value") || [])
        .map((node) => cleanText(node.textContent))
        .filter(Boolean);
      const priceNode = row?.querySelector(".cart-item__prices .price__regular") || row?.querySelector(".cart-item__prices .price");

      return {
        image: image?.currentSrc || image?.src || item.featured_image?.url || item.image || "",
        title: cleanText(row?.querySelector(".cart-item__title")?.textContent) || item.product_title || item.title,
        variant:
          variantValues.join(", ") ||
          (item.variant_title && item.variant_title !== "Default Title" ? item.variant_title : ""),
        price: cleanText(priceNode?.textContent),
        quantity: Number(item.quantity || row?.dataset.quantity || 1),
      };
    }

    setTriggerLoading(trigger, loading) {
      if (!trigger?.isConnected) return;
      trigger.classList.toggle("btn--loading", loading);
      if (loading) trigger.setAttribute("aria-disabled", "true");
      else trigger.removeAttribute("aria-disabled");
    }

    regularLineCount() {
      return this.root.querySelectorAll('a[is="cart-remove-item"][data-index]').length;
    }

    async onRemoveIntent(trigger) {
      if (this.busy || this.intentResolving || trigger.getAttribute("aria-disabled") === "true") return;

      const operation = this.beginOperation();
      this.intentResolving = true;

      this.hideStatus();
      this.setTriggerLoading(trigger, true);

      try {
        const { cart, item, key, index } = await this.itemForTrigger(trigger);
        if (!this.isCurrentOperation(operation)) return;

        const pending = {
          trigger,
          cart,
          item,
          key,
          index,
          lastLine: this.regularLineCount() <= 1,
          visual: this.visualForTrigger(trigger, item),
        };

        const requiresConfirmation = this.isMobile() || Number(item.quantity) > 1;
        this.intentResolving = false;
        if (requiresConfirmation) {
          this.setTriggerLoading(trigger, false);
          this.openConfirmation(pending);
        } else {
          track("li_cart_remove_direct", this.analyticsData(pending));
          await this.removePending(pending, operation);
        }
      } catch (error) {
        if (!this.isCurrentOperation(operation)) return;
        this.setTriggerLoading(trigger, false);
        const retryPending = { trigger };
        this.retryPending = retryPending;
        this.showStatusError(error.message || "Couldn’t remove item. Try again.");
        track("li_cart_remove_error", { error_stage: "snapshot" });
      } finally {
        if (operation === this.operationEpoch) this.intentResolving = false;
      }
    }

    analyticsData(pending, extra) {
      return Object.assign(
        {
          variant_id: String(pending?.item?.variant_id || ""),
          quantity: Number(pending?.item?.quantity || 0),
          cart_item_count: Number(pending?.cart?.item_count || this.cart?.item_count || 0),
          is_last_line: Boolean(pending?.lastLine),
          viewport_mode: this.isMobile() ? "mobile" : "desktop",
        },
        extra || {}
      );
    }

    confirmationCopy(pending) {
      const count = Number(pending.item.quantity || 1);
      if (count > 1) {
        return {
          title: `Remove all ${count} packs?`,
          support: pending.lastLine ? "This will empty your cart." : "This removes the full quantity from your cart.",
        };
      }

      if (pending.lastLine) {
        return { title: "Remove the last item?", support: "You can undo this after removal." };
      }

      return { title: "Remove this item?", support: "" };
    }

    openConfirmation(pending) {
      this.pending = pending;
      this.retryPending = pending;
      const copy = this.confirmationCopy(pending);

      this.confirmView.hidden = false;
      this.recoveryView.hidden = true;
      this.sheet.setAttribute("aria-labelledby", this.title.id);
      this.closeButton.setAttribute("aria-label", "Keep item in cart");
      this.backdrop.setAttribute("aria-label", "Keep item in cart");
      this.recoveryUndo.disabled = true;
      this.shopBaby.inert = true;
      this.shopBaby.tabIndex = -1;
      this.title.textContent = copy.title;
      this.support.textContent = copy.support;
      this.support.hidden = !copy.support;
      this.productTitle.textContent = pending.visual.title;
      this.variant.textContent = pending.visual.variant;
      this.variant.hidden = !pending.visual.variant;
      this.price.textContent = pending.visual.price;
      this.price.hidden = !pending.visual.price;
      this.quantity.textContent = `Qty ${pending.visual.quantity}`;

      if (pending.visual.image) {
        this.image.src = pending.visual.image;
        this.image.alt = pending.visual.title;
        this.image.hidden = false;
      } else {
        this.image.removeAttribute("src");
        this.image.alt = "";
        this.image.hidden = true;
      }

      this.clearConfirmError();
      this.confirmLabel.textContent = "Remove item";
      this.layer.dataset.state = "confirming";
      this.layer.hidden = false;
      this.setBackgroundInert(true);
      this.setConfirmBusy(false);

      track("li_cart_remove_prompt_view", this.analyticsData(pending));

      requestAnimationFrame(() => {
        if (this.isLayerOpen() && this.layer.dataset.state === "confirming") {
          this.cancelButton.focus({ preventScroll: true });
        }
      });
    }

    setBackgroundInert(inert) {
      const selectors = [
        ".drawer__close-btn",
        ".cart-drawer__header",
        "[id^='CartDrawerBody-']",
        "[id^='CartDrawerFooter-']",
        "[id^='CartDrawerEmpty-']",
      ];

      this.root.querySelectorAll(selectors.join(",")).forEach((node) => {
        if (node.closest("[data-li-cart-remove-layer]")) return;
        node.inert = inert;
      });
    }

    restoreDrawerFocus(preferred) {
      this.setBackgroundInert(false);
      const fallback = this.root.querySelector(".cart-item__title") || this.root.querySelector(".drawer__close-btn");
      const target = preferred?.isConnected ? preferred : fallback;
      if (target) target.focus({ preventScroll: true });
    }

    closeLayer({ reason = "dismiss", restoreFocus = true, keepUndo = false, trackCancel = false } = {}) {
      if (this.busy) return;
      const trigger = this.pending?.trigger;
      const pending = this.pending;

      this.layer.hidden = true;
      this.layer.dataset.state = "closed";
      this.confirmView.hidden = false;
      this.recoveryView.hidden = true;
      this.clearConfirmError();
      this.clearRecoveryError();
      this.setBackgroundInert(false);

      if (trackCancel && pending) {
        track("li_cart_remove_cancel", this.analyticsData(pending, { cancel_reason: reason }));
      }

      this.pending = null;
      if (!keepUndo) this.undoItem = null;

      if (restoreFocus) this.restoreDrawerFocus(trigger);
    }

    setConfirmBusy(busy) {
      this.busy = busy;
      this.sheet.setAttribute("aria-busy", busy ? "true" : "false");
      this.confirmButton.disabled = busy;
      this.cancelButton.disabled = busy;
      this.closeButton.disabled = busy;
      this.confirmLabel.textContent = busy ? "Removing…" : this.confirmError.hidden ? "Remove item" : "Retry";
      this.layer.dataset.state = busy ? "removing" : this.confirmError.hidden ? "confirming" : "error";
      if (busy) {
        requestAnimationFrame(() => {
          if (this.isLayerOpen() && !this.root.hidden) this.sheet.focus({ preventScroll: true });
        });
      }
    }

    showConfirmError(message) {
      this.confirmError.textContent = message || "Couldn’t remove item. Try again.";
      this.confirmError.hidden = false;
      this.setConfirmBusy(false);
    }

    clearConfirmError() {
      this.confirmError.textContent = "";
      this.confirmError.hidden = true;
    }

    showRecoveryError(message) {
      this.recoveryError.textContent = message || "Couldn’t restore item. Try again.";
      this.recoveryError.hidden = false;
      this.recoveryUndo.textContent = "Retry Undo";
    }

    clearRecoveryError() {
      this.recoveryError.textContent = "";
      this.recoveryError.hidden = true;
      this.recoveryUndo.textContent = "Undo";
    }

    sectionsToRender() {
      const sections = [];
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:grouped-sections", { bubbles: true, detail: { sections } })
      );
      return Array.from(new Set(sections.filter(Boolean)));
    }

    async cartRequest(url, body) {
      const response = await fetch(url, {
        ...window.FoxTheme.utils.fetchConfig(),
        body: JSON.stringify(body),
      });

      let json;
      try {
        json = await response.json();
      } catch (error) {
        const parseError = new Error("The cart could not be updated. Try again.");
        parseError.ambiguous = response.ok;
        throw parseError;
      }

      if (!response.ok || json?.errors || json?.status >= 400) {
        const message =
          typeof json?.errors === "string"
            ? json.errors
            : typeof json?.description === "string"
              ? json.description
              : "The cart could not be updated. Try again.";
        const responseError = new Error(message);
        responseError.definitive = true;
        throw responseError;
      }

      return json;
    }

    publishCart(cart, meta) {
      return new Promise((resolve) => {
        let settled = false;
        const finish = (event) => {
          if (settled) return;
          settled = true;
          clearTimeout(timeout);
          document.removeEventListener("cart:updated", finish);
          resolve(event?.detail?.cart || cart);
        };
        const timeout = window.setTimeout(() => finish(), 1600);

        document.addEventListener("cart:updated", finish, { once: true });
        window.FoxTheme.pubsub.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          sources: "li-cart-removal-ux",
          cart,
          target: meta?.target,
          line: meta?.line,
          name: meta?.name || "remove",
        });
      });
    }

    async reconcileRemoval(pending) {
      try {
        const cart = await this.fetchCartSnapshot();
        this.cart = cart;
        const baselineQuantity = this.fingerprintQuantity(pending.cart, pending.item);
        const targetQuantity = Math.max(0, baselineQuantity - Number(pending.item.quantity || 0));
        const removed = this.fingerprintQuantity(cart, pending.item) <= targetQuantity;
        return { cart, removed };
      } catch (error) {
        return null;
      }
    }

    normalizedProperties(properties) {
      return Object.fromEntries(
        Object.entries(properties || {})
          .sort(([left], [right]) => left.localeCompare(right))
          .map(([key, value]) => [key, String(value)]),
      );
    }

    itemFingerprint(item) {
      const sellingPlanId = item?.selling_plan_allocation?.selling_plan?.id || item?.selling_plan || "";
      return JSON.stringify({
        variantId: String(item?.variant_id || item?.id || ""),
        properties: this.normalizedProperties(item?.properties),
        sellingPlanId: String(sellingPlanId),
      });
    }

    fingerprintQuantity(cart, item) {
      const fingerprint = this.itemFingerprint(item);
      return (cart?.items || []).reduce(
        (quantity, candidate) =>
          quantity + (this.itemFingerprint(candidate) === fingerprint ? Number(candidate.quantity || 0) : 0),
        0,
      );
    }

    async removePending(pending, existingOperation) {
      if (this.busy) return;

      const operation = existingOperation || this.beginOperation();

      this.retryPending = pending;
      this.hideStatus();
      this.setTriggerLoading(pending.trigger, true);
      if (this.isLayerOpen()) this.setConfirmBusy(true);
      else this.busy = true;

      let cart;
      let needsDrawerRefresh = false;

      try {
        const sections = this.sectionsToRender();
        const body = {
          id: pending.key || pending.item.key,
          quantity: 0,
          sections,
          sections_url: window.location.pathname,
        };

        if (!body.id) {
          delete body.id;
          body.line = pending.index;
        }

        try {
          cart = await this.cartRequest(window.FoxTheme.routes.cart_change_url, body);
        } catch (error) {
          if (error.definitive) throw error;
          const reconciliation = await this.reconcileRemoval(pending);
          if (!reconciliation?.removed) throw error;
          cart = reconciliation.cart;
          needsDrawerRefresh = true;
        }

        this.cart = cart;
        if (!this.isCurrentOperation(operation)) {
          if (needsDrawerRefresh) {
            document.dispatchEvent(new CustomEvent("cart:refresh", { detail: { open: false } }));
          } else {
            await this.publishCart(cart, { target: null, line: pending.index, name: "remove" });
          }
          return;
        }

        if (needsDrawerRefresh) {
          try {
            await this.refreshDrawer();
          } catch (error) {
            document.dispatchEvent(new CustomEvent("cart:refresh", { detail: { open: true } }));
          }
        } else {
          await this.publishCart(cart, { target: null, line: pending.index, name: "remove" });
        }

        if (!this.isCurrentOperation(operation)) return;

        this.undoItem = cloneJson(pending.item);
        this.restoreOperation = null;
        this.busy = false;
        this.setTriggerLoading(pending.trigger, false);
        track("li_cart_remove_success", this.analyticsData(pending, { remaining_line_count: cart.items?.length || 0 }));

        if (pending.lastLine || (cart.items?.length || 0) === 0) {
          this.showRecovery(pending);
        } else {
          if (this.isLayerOpen()) {
            this.layer.hidden = true;
            this.layer.dataset.state = "closed";
            this.setBackgroundInert(false);
          }
          this.pending = null;
          this.showStatusSuccess("Item removed", true);
          requestAnimationFrame(() => this.restoreDrawerFocus());
        }
      } catch (error) {
        if (!this.isCurrentOperation(operation)) return;
        this.busy = false;
        this.setTriggerLoading(pending.trigger, false);
        track("li_cart_remove_error", this.analyticsData(pending, { error_stage: "remove" }));

        if (this.isLayerOpen()) {
          this.showConfirmError("Couldn’t remove item. Try again.");
          requestAnimationFrame(() => this.confirmButton.focus());
        } else {
          this.showStatusError("Couldn’t remove item. Try again.");
        }
      }
    }

    showRecovery(pending) {
      this.pending = pending;
      this.confirmView.hidden = true;
      this.recoveryView.hidden = false;
      this.sheet.setAttribute("aria-labelledby", this.recoveryTitle.id);
      this.closeButton.setAttribute("aria-label", "Close removal message");
      this.backdrop.setAttribute("aria-label", "Close removal message");
      this.clearRecoveryError();
      this.closeButton.disabled = false;
      this.cancelButton.disabled = true;
      this.confirmButton.disabled = true;
      this.recoveryUndo.disabled = false;
      this.shopBaby.removeAttribute("aria-disabled");
      this.shopBaby.inert = false;
      this.shopBaby.removeAttribute("tabindex");
      this.layer.dataset.state = "recovery";
      this.layer.hidden = false;
      this.sheet.setAttribute("aria-busy", "false");
      this.setBackgroundInert(true);

      requestAnimationFrame(() => {
        if (this.isLayerOpen() && this.layer.dataset.state === "recovery") {
          this.shopBaby.focus({ preventScroll: true });
        }
      });
    }

    async refreshDrawer() {
      return new Promise((resolve, reject) => {
        let settled = false;
        const finish = (event) => {
          if (settled) return;
          settled = true;
          clearTimeout(timeout);
          document.removeEventListener("cart:updated", finish);
          const cart = event?.detail?.cart;
          if (cart) this.cart = cart;
          resolve(cart || this.cart);
        };
        const timeout = window.setTimeout(() => {
          if (settled) return;
          settled = true;
          document.removeEventListener("cart:updated", finish);
          reject(new Error("The cart took too long to refresh."));
        }, 5000);

        document.addEventListener("cart:updated", finish, { once: true });
        document.dispatchEvent(new CustomEvent("cart:refresh", { detail: { open: true } }));
      });
    }

    async reconcileUndo(operation) {
      let lastQuantity = null;
      for (let attempt = 0; attempt < 2; attempt += 1) {
        if (attempt > 0) await new Promise((resolve) => window.setTimeout(resolve, 250));
        try {
          const cart = await this.fetchCartSnapshot();
          this.cart = cart;
          lastQuantity = this.fingerprintQuantity(cart, operation.item);
          if (lastQuantity >= operation.targetQuantity) return { state: "committed", cart };
        } catch (error) {
          return { state: "unknown", cart: null };
        }
      }

      return lastQuantity === operation.baselineQuantity
        ? { state: "unchanged", cart: this.cart }
        : { state: "unknown", cart: this.cart };
    }

    async renderRestoredCart(operation, addResponse) {
      if (addResponse?.sections) {
        const cart = await this.fetchCartSnapshot();
        cart.sections = addResponse.sections;
        this.cart = cart;
        await this.publishCart(cart, { target: null, line: null, name: "add" });
        return cart;
      }

      return this.refreshDrawer();
    }

    setUndoBusy(inRecovery, busy) {
      this.busy = busy;
      if (inRecovery) {
        this.sheet.setAttribute("aria-busy", busy ? "true" : "false");
        this.recoveryUndo.disabled = busy;
        this.recoveryUndo.textContent = busy ? "Restoring…" : "Undo";
        this.shopBaby.toggleAttribute("aria-disabled", busy);
        this.shopBaby.inert = busy;
        if (busy) this.shopBaby.tabIndex = -1;
        else this.shopBaby.removeAttribute("tabindex");
        if (busy) {
          requestAnimationFrame(() => {
            if (this.isLayerOpen() && !this.root.hidden) this.sheet.focus({ preventScroll: true });
          });
        }
      } else {
        this.status.dataset.state = busy ? "loading" : this.status.dataset.state;
        if (busy) this.statusMessage.textContent = "Restoring item…";
        this.statusUndo.disabled = busy;
        this.statusClose.disabled = busy;
      }
    }

    showUndoFailure(inRecovery, message, retryLabel = "Retry Undo") {
      this.busy = false;
      if (inRecovery) {
        this.sheet.setAttribute("aria-busy", "false");
        this.recoveryUndo.disabled = false;
        this.recoveryUndo.textContent = retryLabel;
        this.shopBaby.removeAttribute("aria-disabled");
        this.shopBaby.inert = false;
        this.shopBaby.removeAttribute("tabindex");
        this.recoveryError.textContent = message;
        this.recoveryError.hidden = false;
      } else {
        this.statusUndo.disabled = false;
        this.statusClose.disabled = false;
        this.status.dataset.state = "error";
        this.statusMessage.textContent = message;
        this.statusUndo.textContent = retryLabel;
        this.statusUndo.hidden = false;
        this.statusRetry.hidden = true;
        this.status.hidden = false;
        this.status.setAttribute("role", "alert");
        this.status.setAttribute("aria-live", "assertive");
      }
    }

    finishUndo(inRecovery) {
      this.busy = false;
      this.undoItem = null;
      this.restoreOperation = null;
      this.retryPending = null;
      this.pending = null;
      this.statusClose.disabled = false;

      if (inRecovery) {
        this.layer.hidden = true;
        this.layer.dataset.state = "closed";
        this.setBackgroundInert(false);
      }

      this.showStatusSuccess("Item restored", false);
      requestAnimationFrame(() => this.restoreDrawerFocus());
    }

    async undoRemoval() {
      if (this.busy) return;

      const existing = this.restoreOperation;
      const item = existing?.item || (this.undoItem ? cloneJson(this.undoItem) : null);
      if (!item) return;

      const inRecovery = this.isLayerOpen() && this.layer.dataset.state === "recovery";
      const epoch = this.beginOperation();
      const addItem = existing?.addItem || {
        id: item.variant_id || item.id,
        quantity: Number(item.quantity || 1),
      };

      if (!existing) {
        if (item.properties && Object.keys(item.properties).length) addItem.properties = item.properties;
        const sellingPlanId = item.selling_plan_allocation?.selling_plan?.id || item.selling_plan;
        if (sellingPlanId) addItem.selling_plan = sellingPlanId;
      }

      const operation =
        existing ||
        {
          item,
          addItem,
          baselineQuantity: this.fingerprintQuantity(this.cart, item),
          targetQuantity: this.fingerprintQuantity(this.cart, item) + addItem.quantity,
          mutationCommitted: false,
          tracked: false,
        };
      this.restoreOperation = operation;
      this.setUndoBusy(inRecovery, true);

      let addResponse = null;
      try {
        if (!operation.mutationCommitted) {
          try {
            addResponse = await this.cartRequest(window.FoxTheme.routes.cart_add_url, {
              items: [operation.addItem],
              sections: this.sectionsToRender(),
              sections_url: window.location.pathname,
            });
            operation.mutationCommitted = true;
          } catch (error) {
            if (error.definitive) throw error;
            const reconciliation = await this.reconcileUndo(operation);
            if (reconciliation.state === "committed") operation.mutationCommitted = true;
            else if (reconciliation.state === "unchanged") throw error;
            else {
              operation.mutationCommitted = true;
              operation.uncertain = true;
              this.undoItem = null;
              this.showUndoFailure(
                inRecovery,
                "We couldn’t confirm the cart update. Please refresh the cart before trying again.",
                "Refresh cart",
              );
              track("li_cart_remove_error", { error_stage: "undo_ambiguous" });
              return;
            }
          }

          if (operation.mutationCommitted) {
            this.undoItem = null;
            this.retryPending = null;
            if (!operation.tracked) {
              track("li_cart_remove_undo", {
                variant_id: String(operation.addItem.id),
                quantity: operation.addItem.quantity,
                viewport_mode: this.isMobile() ? "mobile" : "desktop",
              });
              operation.tracked = true;
            }
          }
        }

        if (!this.isCurrentOperation(epoch)) {
          if (addResponse?.sections) {
            const cart = await this.fetchCartSnapshot();
            cart.sections = addResponse.sections;
            this.cart = cart;
            await this.publishCart(cart, { target: null, line: null, name: "add" });
          } else {
            document.dispatchEvent(new CustomEvent("cart:refresh", { detail: { open: false } }));
          }
          return;
        }

        await this.renderRestoredCart(operation, addResponse);
        if (!this.isCurrentOperation(epoch)) return;

        if (operation.uncertain) {
          const reconciledCart = await this.fetchCartSnapshot();
          if (!this.isCurrentOperation(epoch)) return;
          const restoredQuantity = this.fingerprintQuantity(reconciledCart, operation.item);
          if (restoredQuantity < operation.targetQuantity) {
            this.restoreOperation = null;
            this.undoItem = cloneJson(operation.item);
            this.showUndoFailure(inRecovery, "Item wasn’t restored. You can try Undo again.", "Retry Undo");
            return;
          }
          operation.uncertain = false;
        }

        this.finishUndo(inRecovery);
      } catch (error) {
        if (!this.isCurrentOperation(epoch)) return;
        track("li_cart_remove_error", {
          error_stage: operation.mutationCommitted ? "undo_render" : "undo",
        });

        if (operation.mutationCommitted) {
          this.undoItem = null;
          this.showUndoFailure(inRecovery, "Item restored. Refresh the cart to see it.", "Refresh cart");
        } else {
          this.showUndoFailure(inRecovery, "Couldn’t restore item. Try again.", "Retry Undo");
        }
      }
    }

    showStatusSuccess(message, undoAvailable) {
      this.status.dataset.state = "success";
      this.statusMessage.textContent = message;
      this.statusUndo.textContent = "Undo";
      this.statusUndo.hidden = !undoAvailable;
      this.statusUndo.disabled = false;
      this.statusClose.disabled = false;
      this.statusRetry.hidden = true;
      this.status.hidden = false;
      this.status.setAttribute("role", "status");
      this.status.setAttribute("aria-live", "polite");
    }

    showStatusError(message) {
      this.status.dataset.state = "error";
      this.statusMessage.textContent = message;
      this.statusUndo.hidden = true;
      this.statusRetry.hidden = false;
      this.status.hidden = false;
      this.status.setAttribute("role", "alert");
      this.status.setAttribute("aria-live", "assertive");
    }

    hideStatus() {
      this.status.hidden = true;
      this.status.dataset.state = "closed";
      this.statusMessage.textContent = "";
      this.statusUndo.hidden = false;
      this.statusUndo.disabled = false;
      this.statusUndo.textContent = "Undo";
      this.statusClose.disabled = false;
      this.statusRetry.hidden = true;
      this.status.setAttribute("role", "status");
      this.status.setAttribute("aria-live", "polite");
    }

    onRootClick(event) {
      const target = event.target;
      const removeTrigger = target.closest?.('a[is="cart-remove-item"]');

      if (removeTrigger && this.root.contains(removeTrigger)) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        this.onRemoveIntent(removeTrigger);
        return;
      }

      const outerOverlay = this.root.querySelector(":scope > .fixed-overlay");
      if (this.isLayerOpen() && target === outerOverlay) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        this.dismissLayer("outside");
        return;
      }

      if (target.closest?.("[data-li-cart-remove-backdrop]")) {
        event.preventDefault();
        this.dismissLayer("backdrop");
        return;
      }

      if (target.closest?.("[data-li-cart-remove-close]")) {
        event.preventDefault();
        this.dismissLayer("close");
        return;
      }

      if (target.closest?.("[data-li-cart-remove-cancel]")) {
        event.preventDefault();
        this.dismissLayer("keep");
        return;
      }

      if (target.closest?.("[data-li-cart-remove-confirm]")) {
        event.preventDefault();
        if (!this.pending || this.busy) return;
        track("li_cart_remove_confirm", this.analyticsData(this.pending));
        this.removePending(this.pending);
        return;
      }

      if (target.closest?.("[data-li-cart-recovery-undo]") || target.closest?.("[data-li-cart-status-undo]")) {
        event.preventDefault();
        this.undoRemoval();
        return;
      }

      if (target.closest?.("[data-li-cart-status-retry]")) {
        event.preventDefault();
        if (this.retryPending?.item) this.removePending(this.retryPending);
        else if (this.retryPending?.trigger) this.onRemoveIntent(this.retryPending.trigger);
        return;
      }

      if (target.closest?.("[data-li-cart-status-close]")) {
        event.preventDefault();
        if (this.busy) return;
        this.undoItem = null;
        this.restoreOperation = null;
        this.retryPending = null;
        this.hideStatus();
        return;
      }

      const shopLink = target.closest?.("[data-li-cart-shop-baby]");
      if (shopLink) {
        if (this.busy || shopLink.getAttribute("aria-disabled") === "true") {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          return;
        }
        track("li_cart_remove_recovery_click", {
          destination_path: new URL(this.shopBaby.href, window.location.origin).pathname,
          viewport_mode: this.isMobile() ? "mobile" : "desktop",
        });
      }
    }

    dismissLayer(reason) {
      if (this.busy) return;
      if (this.layer.dataset.state === "recovery") {
        const hasUndo = Boolean(this.undoItem);
        const hasRestoreRefresh = Boolean(this.restoreOperation);
        this.closeLayer({ reason, restoreFocus: false, keepUndo: hasUndo || hasRestoreRefresh });
        if (hasUndo) this.showStatusSuccess("Item removed", true);
        else if (hasRestoreRefresh) {
          this.showUndoFailure(false, "Item restored. Refresh the cart to see it.", "Refresh cart");
        }
        return;
      }

      this.closeLayer({ reason, restoreFocus: true, keepUndo: false, trackCancel: true });
    }

    visibleSheetFocusables() {
      const selector = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
      return Array.from(this.sheet.querySelectorAll(selector)).filter((element) => {
        if (element.closest("[hidden], [inert]")) return false;
        if (element.getAttribute("aria-disabled") === "true") return false;
        const style = window.getComputedStyle(element);
        return style.display !== "none" && style.visibility !== "hidden" && element.getClientRects().length > 0;
      });
    }

    onKeydown(event) {
      if (!this.isLayerOpen() || event.key !== "Tab") return;
      const focusable = this.visibleSheetFocusables();
      if (!focusable.length) {
        event.preventDefault();
        this.sheet.focus({ preventScroll: true });
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.sheet)) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    }

    onKeyup(event) {
      if (!this.isLayerOpen() || event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.dismissLayer("escape");
    }

    reset() {
      this.beginOperation();
      this.busy = false;
      this.intentResolving = false;
      this.pending = null;
      this.retryPending = null;
      this.undoItem = null;
      this.restoreOperation = null;
      this.layer.hidden = true;
      this.layer.dataset.state = "closed";
      this.sheet.setAttribute("aria-busy", "false");
      this.confirmButton.disabled = false;
      this.cancelButton.disabled = false;
      this.closeButton.disabled = false;
      this.recoveryUndo.disabled = true;
      this.shopBaby.inert = true;
      this.shopBaby.tabIndex = -1;
      this.setBackgroundInert(false);
      this.hideStatus();
      this.statusClose.disabled = false;
    }
  }

  function init() {
    const root = document.getElementById("CartDrawer");
    if (!root || !window.FoxTheme?.routes || !window.FoxTheme?.pubsub) return;

    const controller = new LiveIndianlyCartRemovalUx(root);
    if (!controller.ready) return;
    window[INSTANCE_KEY] = controller;
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init, { once: true });
  else init();
})();
